import { Search, CircleX, Stethoscope } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import DataTablePagination from "../DataTablePagination";
import Spacing from "../Spacing";
import TabMenu from "../TabMenu";
import { getLocation } from "../../services/appointment";
import { fetchScanList,fetchMedmoOrderList } from "../../services/order";
import OrderDataTable from "../DataTable/OrderDataTable";
import {
  formatDate,
  
  numberWithCommas,
  generateOrderTableData,
  debounce,
  userMessageTemplates,
} from "../../helpers";
import { useDocumentTitle } from "../../hooks/customDocumentTitleHook.jsx";
import { getPractice } from "../../helpers";


export default function OrderDatabase2({selectedPracticeChange, fetchChangePracticeData, smsLimit, setSmsLimit}) {
  let location_id = getPractice("location_id");
  let account_id = getPractice("account_id");
  let user_id = getPractice("user_id");
  if(selectedPracticeChange != null && fetchChangePracticeData){
    location_id = selectedPracticeChange?.location_id;
    account_id = selectedPracticeChange?.account_id;
    user_id = selectedPracticeChange?.user_id;
  }

  const [dataLoading, setDataLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [scanList, setScanData] = useState([]);
  const [messageTemplate] = useState(userMessageTemplates());
  const [selectAll, setSelectAll] = useState(false);
  const [selectedValue, setSelectedValue] = useState(1);
  const [heading, setHeading] = useState("Imaging Orders");
  const [filter, setFilter] = useState({
    page: 1,
    per_page: 10,
    status: "",
    appointment_type: "",
    search_term: "",
    start_date: "",
    end_date: "",
    location: "",
    account_id: account_id,
    location_id: location_id,
    user_id: user_id,
    order_by: [],
    order_type: selectedValue,
  });

  const [paginationCount, setPaginationCount] = useState({
    totalCount: 0,
    totalPages: 0,
    requestedCount: 0,
    completedCount: 0,
    closedCount: 0,
    in_ProgressCount: 0,
    scheduledCount: 0,
    report_UploadedCount: 0,
    on_HoldCount :0,
    account_id: account_id,
    location_id: location_id,
  });
  const [allLocation, setAllLocation] = useState([]);

  const handleDate = (date) => {
    setDataLoading(true);
    setFilter({
      ...filter,
      start_date: formatDate(date),
    });
  };

  const fetchScanData = async () => {
    const response = await fetchScanList();
    if (response) {
      const generateScanData = response.data
      setScanData(generateScanData);
    }
  };

  useEffect(() => {
    loadOrders(filter);
  }, [filter]);

  useEffect(() => {
    fetchScanData();
  }, []);
  
  useEffect(() => {
    setFilter({
      ...filter,
      account_id: account_id,
      location_id: location_id,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  
  // Loads the locations asynchronously.
  const loadLocations = async (id, accountId) => {
    try {
      const locationResponse = await getLocation(id, accountId);
      setAllLocation(locationResponse.data || []);
    } catch (error) {
      // console.log("🚀 ~ loadLocations ~ error:", error);
    }
  };

  // Loads orders based on the provided filter.
  const loadOrders = async (filter) => {
    setFilter(filter);
    let isCanceled = false;

    try {
      const orderResponse = await fetchMedmoOrderList(
        filter        
      );
      const generateData = generateOrderTableData(
        orderResponse.data.results
      );
      setOrders(generateData);
      setPaginationCount({
        totalCount: orderResponse.data.count,
        totalPages: orderResponse.data.total_pages,
        requestedCount: orderResponse.data.status_counts.Requested,
        completedCount: orderResponse.data.status_counts.Completed,
        closedCount: orderResponse.data.status_counts.Closed,
        in_ProgressCount: orderResponse.data.status_counts.In_Progress,
        scheduledCount: orderResponse.data.status_counts.Scheduled,
        report_UploadedCount: orderResponse.data.status_counts.Report_Uploaded,
        on_HoldCount: orderResponse.data.status_counts.On_Hold,
      });
    } catch (error) {
      if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
        isCanceled = true;
      } else {
        // console.log("🚀 ~ loadOrders ~ error:", error);
      }
    } finally {
      if (!isCanceled) {
        setDataLoading(false);
      }
    }
  };

  // Handles the checkbox change event for a specific appointment.
  const handleCheckboxChange = (id) => {
    setOrders((prevData) =>
      prevData.map((row) =>
        row.id === id ? { ...row, checked: !row.checked } : row
      )
    );
  };

  // Handles the select all functionality for appointments.
  const handleSelectAll = () => {
    const newData = orders.map((row) => ({
      ...row,
      checked: !selectAll,
    }));
    setOrders(newData);
    setSelectAll(!selectAll);
  };

  // Retrieves orders based on the provided filter.
  const getOrdersByStatus = (selectedTabValue) => {
    const tab = selectedTabValue.split(" ")[0];
    
    const tabValue = tab.toLowerCase() === "all" ? "" : tab.toLowerCase();
    setFilter({ ...filter, status: tabValue, page: 1 });
    setHeading((tab === "All" ? "" : tab.replace(/_/g, "-") + " ") + "Imaging Orders");
    // loadOrders({ ...filter, status: tabValue, page: 1 });
  };

  // Handles the page change event.
  const handlePageChange = (newPage) => {
    setDataLoading(true);
    setFilter({ ...filter, page: newPage });
  };

  // Handles the change event for the search term input field.
  const debouncedSetFilter = useCallback(
    debounce((newFilter) => setFilter(newFilter), 300), []
  );

  const handleChangeSearchTerm = (event) => {
    const newFilter = { ...filter, search_term: event.target.value, page: 1 };
    setDataLoading(true);
    debouncedSetFilter(newFilter);
  };

  // Handles the change of rows per page in the filter.
  const handleChangeRowsPerPage = (value) => {
    setDataLoading(true);
    setFilter({
      ...filter,
      page: 1,
      per_page: value,
    });
  };

  // Array containing the tab menu data.
  const tabMenuData = [
    `All (${paginationCount.totalCount})`,
    `Requested (${paginationCount.requestedCount})`,
    `Completed (${paginationCount.completedCount})`,
    `Closed (${paginationCount.closedCount})`,
    `In_Progress (${paginationCount.in_ProgressCount})`,
    `Scheduled (${paginationCount.scheduledCount})`,
    `Report_Uploaded (${paginationCount.report_UploadedCount})`,
    `On_Hold (${paginationCount.on_HoldCount})`,
  ];

  const handleSelectLocation = (e) => {
    setDataLoading(true);
    setFilter({ ...filter, location_id: e.target.value });
  };

  useEffect(() => {
    const sameLocation = filter.location_id === location_id;
    const sameAccount = filter.account_id === account_id;

    if (sameLocation && sameAccount) return;

    setFilter({
      ...filter,
      location_id: location_id,
      account_id: account_id,
    });
    loadLocations(account_id);
    setDataLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location_id, account_id]);

  useDocumentTitle("Imaging Orders");

  return (
    <>
      <div className="docpro_page_heading_wrap">
        <h2 className="docpro_page_heading docpro_fs_24">
          <i className="docpro_teal_bg docpro_white_color">
            <Stethoscope width={20} height={20} />
          </i>
          {heading}
          <span>{numberWithCommas(paginationCount.totalCount)}</span>
        </h2>
      </div>
      <Spacing lg="4" sm="4" />
      <TabMenu
        tabMenuData={tabMenuData}
        filter={false}
        handleSelectTab={getOrdersByStatus}
        setDataLoading={setDataLoading}
      />
      <Spacing lg="12" sm="12" />
      <div className="docpro_data_table_wrap">
        <div className="docpro_data_table_headings">
          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <form className="docpro_search_form">
              <input
                type="text"
                placeholder="Search"
                className="docpro_search_input"
                onChange={handleChangeSearchTerm}
              />
              <button className="docpro_search_btn">
                <Search width={20} height={20} />
              </button>
            </form>
            {filter.start_date && (
              <span onClick={() => handleDate("")}>
                <CircleX size={22} color="var(--accent)" />
              </span>
            )}
          </div>
          {/* <select
              className="docpro_form_field"
              style={{ width: "150px", padding: '5px 27px 5px 12px' }}
              onChange={handleSelectLocation}
              value={filter.location_id}
            >
              <option value="">All Locations</option>
              {allLocation.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </div> */}
        </div>
        <OrderDataTable
          tableData={orders}
          handleSelectAll={handleSelectAll}
          handleCheckboxChange={handleCheckboxChange}
          selectAll={selectAll}
          reloadData={loadOrders}
          reloadDataFilter={filter}
          messageTemplate={messageTemplate}
          user_id={user_id}
          account_id={account_id}
          location_id={location_id}
          scanList={scanList}
          smsLimit={smsLimit}
          setSmsLimit={setSmsLimit}
          dataLoading={dataLoading}
          setDataLoading={setDataLoading}
        />
        {(orders.length > 0 && !dataLoading) && (
          <DataTablePagination
            currentPage={filter.page}
            totalPages={paginationCount.totalPages}
            onPageChange={handlePageChange}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            totalRecords={paginationCount.totalCount}
            currentPageData={filter.per_page}
          />
        )}
      </div>
    </>
  );
}
